/**
 * SensorConfigSection — Self-contained sensor CRUD UI for DevicesSettings.
 * Manages its own state and API calls. Props: { device, isAdmin, onDeviceUpdate }
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  Loader2,
  ChevronDown,
  ChevronUp,
  Play,
  X,
  Activity,
  HelpCircle,
  Layers,
} from 'lucide-react';
import api from '@/lib/api';
import { orgs as orgsApi } from '@/lib/trackizy';
import { devicesActions, orgActions } from '@/store';
import { showToast } from '@/components/shared/Toast';
import { useConfirmDialog } from '@/components/shared/ConfirmDialog';
import EmptyState from '@/components/shared/EmptyState';
import {
  parseSensorConfig,
  colorStatusToHex,
  SENSOR_ICONS,
  resolveSensorIcon,
  SENSOR_TEMPLATES,
} from '@/lib/sensorUtils';
import { composeExpression, decomposeExpression } from '@/lib/sensorTransform';

const OUTPUT_TYPES = ['Number', 'Boolean', 'String'];
const COLOR_OPTIONS = [
  { value: 'green',  label: 'Green',  hex: '#10b981' },
  { value: 'yellow', label: 'Yellow', hex: '#f59e0b' },
  { value: 'red',    label: 'Red',    hex: '#ef4444' },
];

const inputClass =
  'w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10';
const selectClass =
  'w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 bg-white dark:bg-slate-800';
const labelClass =
  'block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1';
const sectionClass =
  'text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4';

function emptyForm() {
  return {
    name: '',
    attribute: '',
    expression: '',
    type: 'Number',
    unit: '',
    icon: 'Activity',
    validation: { min: '', max: '' },
    intervals: [],
    // GPSgate-style quick transforms. Authoritative expression lives in
    // form.expression; this is a UI-side convenience that composes/decomposes
    // the common shapes (invert / scale * value + offset).
    transform: { invert: false, scale: 1, offset: 0 },
  };
}

function formFromTemplate(tpl) {
  const decomposed = decomposeExpression(tpl.expression, tpl.attribute, tpl.type);
  return {
    name: tpl.name,
    attribute: tpl.attribute,
    expression: tpl.expression,
    type: tpl.type,
    unit: tpl.unit,
    icon: tpl.icon,
    validation: tpl.validation
      ? { min: String(tpl.validation.min), max: String(tpl.validation.max) }
      : { min: '', max: '' },
    intervals: tpl.intervals.map((iv) => ({
      threshold: iv.threshold != null ? String(iv.threshold) : '',
      color: iv.color,
    })),
    transform: {
      invert: !!decomposed.invert,
      scale: decomposed.scale,
      offset: decomposed.offset,
    },
  };
}

function formFromSensor(sensor) {
  const decomposed = decomposeExpression(sensor.expression || '', sensor.attribute || '', sensor.type || 'Number');
  return {
    name: sensor.name || '',
    attribute: sensor.attribute || '',
    expression: sensor.expression || '',
    type: sensor.type || 'Number',
    unit: sensor.unit || '',
    icon: sensor.icon || 'Activity',
    validation: sensor.validation
      ? { min: String(sensor.validation.min ?? ''), max: String(sensor.validation.max ?? '') }
      : { min: '', max: '' },
    intervals: (sensor.intervals || []).map((iv) => ({
      threshold: iv.threshold != null ? String(iv.threshold) : '',
      color: iv.color || 'green',
    })),
    transform: {
      invert: !!decomposed.invert,
      scale: decomposed.scale,
      offset: decomposed.offset,
    },
  };
}

function buildSensorEntry(form, traccarAttributeId) {
  const parseNum = (v) => (v !== '' && v != null ? Number(v) : null);
  const validation =
    form.validation.min !== '' || form.validation.max !== ''
      ? { min: parseNum(form.validation.min), max: parseNum(form.validation.max) }
      : null;
  const intervals = form.intervals
    .filter((iv) => iv.color)
    .map((iv) => ({
      threshold: iv.threshold !== '' ? Number(iv.threshold) : null,
      color: iv.color,
    }));
  return {
    name: form.name.trim(),
    attribute: form.attribute.trim(),
    expression: form.expression.trim(),
    type: form.type,
    unit: form.unit,
    icon: form.icon,
    validation,
    intervals,
    traccarAttributeId,
  };
}

// ── SensorForm ─────────────────────────────────────────────────────────────────

function SensorForm({
  device,
  form,
  setForm,
  editingSensor,
  onSaved,
  onCancel,
  isAdmin,
  userTemplates,
  onSaveTemplate,
  onRemoveTemplate,
  onApplyTemplateToDevices,
  attributeKeyOptions = [],
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showSyntaxCheatSheet, setShowSyntaxCheatSheet] = useState(false);

  const handleIntervalChange = (idx, field, value) => {
    setForm((prev) => {
      const intervals = [...prev.intervals];
      intervals[idx] = { ...intervals[idx], [field]: value };
      return { ...prev, intervals };
    });
  };

  const addInterval = () => {
    if (form.intervals.length >= 3) return;
    setForm((prev) => ({
      ...prev,
      intervals: [...prev.intervals, { threshold: '', color: 'green' }],
    }));
  };

  const removeInterval = (idx) => {
    setForm((prev) => ({
      ...prev,
      intervals: prev.intervals.filter((_, i) => i !== idx),
    }));
  };

  const handleTest = async () => {
    if (!device?.id) return;
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const result = await api.testComputedAttribute(device.id, {
        description: form.name.trim() || 'test',
        attribute: form.attribute.trim(),
        expression: form.expression.trim(),
        type: form.type,
      });
      setTestResult(result != null ? String(result) : 'null');
    } catch (err) {
      setTestError(err.message || t('sensors.testError'));
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.attribute.trim() || !form.expression.trim()) return;
    setSaving(true);
    try {
      await onSaved(form);
    } finally {
      setSaving(false);
    }
  };

  const IconComp = resolveSensorIcon(form.icon);

  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-4 mt-3">
      {/* How it works — collapsible in-context guide. Opens with a worked
          example (seatbelt) so installers don't need to leave the drawer
          to figure out what each field does. */}
      <div className="rounded-xl border border-blue-200/60 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/10 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowHelp((p) => !p)}
          className="w-full flex items-center gap-2 px-3 py-2 text-left text-blue-700 dark:text-blue-300 hover:bg-blue-100/40 dark:hover:bg-blue-900/20 transition-colors"
        >
          <HelpCircle size={13} />
          <span className="text-[11px] font-semibold uppercase tracking-wider">
            {t('sensors.howItWorksTitle', 'How sensor mapping works')}
          </span>
          <span className="ms-auto text-blue-400 dark:text-blue-500">
            {showHelp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </button>
        {showHelp && (
          <div className="px-4 pb-4 pt-1 text-[11px] leading-relaxed text-slate-700 dark:text-slate-300 space-y-3">
            <p>
              {t('sensors.helpIntro',
                'A sensor maps a raw value the device reports (an attribute key like din2 or io66) to a labeled, optionally-transformed readout on the dashboard. The Telemetry strip on the Live Map and the Vehicle list both render configured sensors as named cards.')}
            </p>

            <div>
              <div className="font-semibold text-slate-800 dark:text-slate-200 mb-1">
                {t('sensors.helpStepsTitle', 'Four-step recipe')}
              </div>
              <ol className="list-decimal list-inside space-y-0.5 marker:text-blue-500">
                <li>{t('sensors.helpStep1', 'Pick the raw key in Output Attribute — the autocomplete is fed from what this device actually reported.')}</li>
                <li>{t('sensors.helpStep2', 'Set Sensor Name (the label users see), Type (Number / Boolean / String), Unit, and Icon.')}</li>
                <li>{t('sensors.helpStep3', 'Use Quick Transforms to invert a boolean or apply scale × value + offset on a number. Click "Apply to expression →" to rewrite the formula. Custom JEXL formulas can be typed straight into the Expression field.')}</li>
                <li>{t('sensors.helpStep4', 'Click Test to evaluate against the latest position. Optionally add Color Intervals (numeric sensors only) to color the card by value. Save.')}</li>
              </ol>
            </div>

            <div className="rounded-lg border border-blue-200 dark:border-blue-800/60 bg-white dark:bg-slate-900/50 p-3 space-y-2">
              <div className="font-semibold text-slate-800 dark:text-slate-200">
                {t('sensors.helpExampleTitle', 'Worked example — Seatbelt sensor')}
              </div>
              <p className="text-slate-600 dark:text-slate-400">
                {t('sensors.helpExampleIntro',
                  'A Teltonika unit has the buckle wired to Digital Input 2 and reports din2: true when buckled, false when not. We want a labeled Seatbelt sensor on the panel.')}
              </p>
              <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 font-mono text-[10.5px]">
                <span className="text-slate-500">Sensor Name</span><span>Seatbelt</span>
                <span className="text-slate-500">Output Attribute</span><span>din2</span>
                <span className="text-slate-500">Output Type</span><span>Boolean</span>
                <span className="text-slate-500">Quick Transform</span><span>Invert ON if buckle pulls active-low</span>
                <span className="text-slate-500">Expression</span><span>din2  (or !din2 with invert)</span>
                <span className="text-slate-500">Unit / Range</span><span>— (booleans skip)</span>
                <span className="text-slate-500">Color Intervals</span><span>— (booleans render On/Off)</span>
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-500 italic">
                {t('sensors.helpExampleTip',
                  'Tip: once it works, click "Save as template" and apply to every other Teltonika in your fleet with one click.')}
              </p>
            </div>

            <div>
              <div className="font-semibold text-slate-800 dark:text-slate-200 mb-1">
                {t('sensors.helpCommonShapesTitle', 'Common shapes')}
              </div>
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-[10.5px] font-mono">
                  <thead className="text-slate-500 text-left">
                    <tr>
                      <th className="px-1 py-0.5 font-semibold">Sensor</th>
                      <th className="px-1 py-0.5 font-semibold">Type</th>
                      <th className="px-1 py-0.5 font-semibold">Attribute</th>
                      <th className="px-1 py-0.5 font-semibold">Expression</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700 dark:text-slate-300">
                    <tr><td className="px-1 py-0.5">Door</td><td>Boolean</td><td>din1</td><td>din1 (or !din1)</td></tr>
                    <tr><td className="px-1 py-0.5">Panic</td><td>Boolean</td><td>din3</td><td>din3</td></tr>
                    <tr><td className="px-1 py-0.5">Fuel %</td><td>Number</td><td>adc1</td><td>adc1 * 0.024</td></tr>
                    <tr><td className="px-1 py-0.5">Coolant °C</td><td>Number</td><td>coolantTemp</td><td>coolantTemp - 40</td></tr>
                    <tr><td className="px-1 py-0.5">RPM</td><td>Number</td><td>rpm</td><td>rpm * 0.25</td></tr>
                    <tr><td className="px-1 py-0.5">Battery V</td><td>Number</td><td>power</td><td>power * 0.001</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[10px] text-slate-500 dark:text-slate-500">
              {t('sensors.helpFootnote',
                'Saving creates a computed attribute, links it to this device, and stores the config on the device. Templates are shared across the organization so every admin can reuse them.')}
            </div>
          </div>
        )}
      </div>

      {/* Template picker — built-in templates on top row, user-saved templates
          on a second row when present. Built-in templates are read-only;
          user templates can be applied to current form, applied bulk to
          other devices, or deleted. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {t('sensors.fromTemplate')}:
          </span>
          {SENSOR_TEMPLATES.map((tpl) => (
            <button
              key={tpl.name}
              type="button"
              onClick={() => setForm(formFromTemplate(tpl))}
              className="h-7 px-3 text-[10px] font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              {tpl.name}
            </button>
          ))}
        </div>
        {userTemplates && userTemplates.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {t('sensors.myTemplates', 'My templates')}:
            </span>
            {userTemplates.map((tpl) => (
              <div key={tpl.id} className="flex items-center h-7 rounded-lg border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-900/20 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setForm(formFromTemplate(tpl.sensor))}
                  className="h-full px-3 text-[10px] font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                  title={t('sensors.useTemplate', 'Use this template')}
                >
                  {tpl.name}
                </button>
                {onApplyTemplateToDevices && (
                  <button
                    type="button"
                    onClick={() => onApplyTemplateToDevices(tpl)}
                    className="h-full px-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border-l border-blue-200 dark:border-blue-800/60"
                    title={t('sensors.applyTemplateToDevices', 'Apply to other devices…')}
                  >
                    <Activity size={10} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveTemplate && onRemoveTemplate(tpl.id)}
                  className="h-full px-2 text-blue-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-l border-blue-200 dark:border-blue-800/60"
                  title={t('sensors.removeTemplate', 'Delete template')}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Name */}
        <div>
          <label className={labelClass}>{t('sensors.name')} *</label>
          <input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            required
            className={inputClass}
            placeholder={t('sensors.name')}
          />
        </div>

        {/* Attribute — combobox: free-text input backed by a datalist of
            keys the device's latest position actually reports + every
            attribute key referenced by a built-in template. Falls back
            cleanly when the device is offline (datalist is just empty). */}
        <div>
          <label className={labelClass}>{t('sensors.attribute')} *</label>
          <input
            list={device?.id ? `sensor-attr-keys-${device.id}` : undefined}
            value={form.attribute}
            onChange={(e) => setForm((p) => ({ ...p, attribute: e.target.value }))}
            required
            className={inputClass}
            placeholder={
              attributeKeyOptions.length
                ? t('sensors.attributeHintWithSuggestions', 'Pick from device or type custom…')
                : t('sensors.attributeHint')
            }
            autoComplete="off"
          />
          {device?.id && attributeKeyOptions.length > 0 && (
            <datalist id={`sensor-attr-keys-${device.id}`}>
              {attributeKeyOptions.map((k) => (
                <option key={k} value={k} />
              ))}
            </datalist>
          )}
          {attributeKeyOptions.length > 0 && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
              {t('sensors.attributeKeysCount', '{{n}} keys this device has reported', {
                n: attributeKeyOptions.length,
              })}
            </p>
          )}
        </div>

        {/* Type */}
        <div>
          <label className={labelClass}>{t('sensors.type')}</label>
          <select
            value={form.type}
            onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
            className={selectClass}
          >
            {OUTPUT_TYPES.map((t2) => (
              <option key={t2} value={t2}>{t2}</option>
            ))}
          </select>
        </div>

        {/* Unit */}
        <div>
          <label className={labelClass}>{t('sensors.unit')}</label>
          <input
            value={form.unit}
            onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
            className={inputClass}
            placeholder={t('sensors.unitHint')}
          />
        </div>

        {/* Icon picker */}
        <div>
          <label className={labelClass}>{t('sensors.icon')}</label>
          <select
            value={form.icon}
            onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
            className={selectClass}
          >
            {SENSOR_ICONS.map((si) => (
              <option key={si.key} value={si.key}>{si.label}</option>
            ))}
          </select>
        </div>

        {/* Validation */}
        <div>
          <label className={labelClass}>{t('sensors.validRange')}</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={form.validation.min}
              onChange={(e) => setForm((p) => ({ ...p, validation: { ...p.validation, min: e.target.value } }))}
              placeholder={t('sensors.min')}
              className={inputClass}
            />
            <span className="text-slate-400 text-xs flex-shrink-0">–</span>
            <input
              type="number"
              value={form.validation.max}
              onChange={(e) => setForm((p) => ({ ...p, validation: { ...p.validation, max: e.target.value } }))}
              placeholder={t('sensors.max')}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Quick transforms — composes the expression below for the common
          GPSgate-style shapes (invert / scale * value + offset). For
          formulas these don't recognise, the expression field stays
          authoritative and the "Custom expression" badge appears. */}
      {(() => {
        const decomposed = decomposeExpression(form.expression, form.attribute, form.type);
        const isCustom = decomposed.custom;
        const setTransform = (patch) => setForm((p) => ({ ...p, transform: { ...p.transform, ...patch } }));
        const apply = () => {
          const next = composeExpression(form.attribute, { ...form.transform, type: form.type });
          setForm((p) => ({ ...p, expression: next }));
        };
        return (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {t('sensors.quickTransforms', 'Quick transforms')}
              </span>
              {isCustom && (
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-semibold uppercase tracking-wider"
                  title={t('sensors.customExpressionHint', 'Expression contains a custom formula that does not match the simple shapes — these fields will not auto-sync until you Apply.')}>
                  {t('sensors.customExpression', 'Custom expression')}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-end">
              {form.type === 'Boolean' ? (
                <label className="flex items-center gap-2 h-9 text-xs text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={!!form.transform.invert}
                    onChange={(e) => setTransform({ invert: e.target.checked })}
                    className="rounded border-slate-300 text-blue-500 focus:ring-blue-400"
                  />
                  {t('sensors.invertBoolean', 'Invert (active-low)')}
                </label>
              ) : (
                <>
                  <div>
                    <label className={labelClass}>{t('sensors.scale', 'Scale')}</label>
                    <input
                      type="number"
                      step="any"
                      value={form.transform.scale ?? 1}
                      onChange={(e) => setTransform({ scale: e.target.value === '' ? 1 : Number(e.target.value) })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{t('sensors.offset', 'Offset')}</label>
                    <input
                      type="number"
                      step="any"
                      value={form.transform.offset ?? 0}
                      onChange={(e) => setTransform({ offset: e.target.value === '' ? 0 : Number(e.target.value) })}
                      className={inputClass}
                    />
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={apply}
                disabled={!form.attribute.trim()}
                className="h-9 px-3 text-[11px] font-semibold rounded-xl text-white bg-slate-700 hover:bg-slate-800 disabled:opacity-40"
                title={t('sensors.applyToExpressionHint', 'Rewrite the expression below using these transform values')}
              >
                {t('sensors.applyToExpression', 'Apply to expression →')}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-500">
              {form.type === 'Boolean'
                ? t('sensors.transformHintBool', 'Toggle invert when the device wires ignition active-low. Click Apply to rewrite the expression.')
                : t('sensors.transformHintNumeric', 'Result = (raw value × scale) + offset. Leave scale = 1, offset = 0 for no transform.')}
            </p>
          </div>
        );
      })()}

      {/* Expression */}
      <div className="relative">
        <div className="flex items-center gap-1 mb-1">
          <label className={`${labelClass} mb-0`}>{t('sensors.expression')} *</label>
          <button
            type="button"
            aria-label={t('sensors.syntaxHelpAria', 'Show syntax examples')}
            onClick={() => setShowSyntaxCheatSheet((p) => !p)}
            className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title={t('sensors.syntaxHelpTitle', 'Show JEXL syntax examples (click to insert)')}
          >
            <HelpCircle size={12} />
          </button>
          <span className="text-[10px] text-slate-400 dark:text-slate-500 italic">
            {t('sensors.syntaxHelpHint', 'JEXL expression — click ⓘ for examples')}
          </span>
        </div>
        {showSyntaxCheatSheet && (
          <SyntaxCheatSheetPopover
            onClose={() => setShowSyntaxCheatSheet(false)}
            onInsert={(snippet) => {
              setForm((p) => {
                const current = (p.expression || '').trim();
                // Empty field → replace. Otherwise append at the end.
                const next = current ? `${current} ${snippet}` : snippet;
                return { ...p, expression: next };
              });
            }}
          />
        )}
        <div className="flex gap-2 items-start">
          <textarea
            value={form.expression}
            onChange={(e) => setForm((p) => ({ ...p, expression: e.target.value }))}
            required
            rows={2}
            placeholder={t('sensors.expressionHint')}
            className="flex-1 px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 resize-y font-mono"
          />
          {isAdmin && device?.id && (
            <div className="flex flex-col gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={handleTest}
                disabled={testing || !form.expression.trim()}
                className="h-9 px-3 text-xs font-semibold rounded-xl text-white bg-slate-600 hover:bg-slate-700 disabled:opacity-40 flex items-center gap-1.5"
              >
                {testing ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                {t('sensors.testExpression')}
              </button>
              {testResult != null && (
                <span className="text-[10px] px-2 py-1 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-mono">
                  {t('sensors.testResult')}: {testResult}
                </span>
              )}
              {testError && (
                <span className="text-[10px] px-2 py-1 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                  {t('sensors.testError')}: {testError}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Color Intervals */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className={labelClass + ' mb-0'}>{t('sensors.colorIntervals')}</label>
          {form.intervals.length < 3 && (
            <button
              type="button"
              onClick={addInterval}
              className="h-6 px-2 text-[10px] font-semibold rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors flex items-center gap-1"
            >
              <Plus size={10} />
              {t('sensors.addInterval')}
            </button>
          )}
        </div>
        {form.intervals.length === 0 && (
          <p className="text-[10px] text-slate-400 dark:text-slate-500 italic">
            {t('sensors.noSensorsDesc')} — {t('sensors.addInterval')}
          </p>
        )}
        <div className="space-y-2">
          {form.intervals.map((iv, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="flex-1">
                <input
                  type="number"
                  value={iv.threshold}
                  onChange={(e) => handleIntervalChange(idx, 'threshold', e.target.value)}
                  placeholder={t('sensors.threshold') + ' (≥)'}
                  className={inputClass}
                />
              </div>
              <div className="flex-1">
                <select
                  value={iv.color}
                  onChange={(e) => handleIntervalChange(idx, 'color', e.target.value)}
                  className={selectClass}
                >
                  {COLOR_OPTIONS.map((co) => (
                    <option key={co.value} value={co.value}>{co.label}</option>
                  ))}
                </select>
              </div>
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 border border-white/20"
                style={{ background: COLOR_OPTIONS.find((c) => c.value === iv.color)?.hex || '#64748b' }}
              />
              <button
                type="button"
                onClick={() => removeInterval(idx)}
                className="p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 transition-colors"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        {form.intervals.length > 0 && (
          <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
            Leave threshold empty for the catch-all interval (always matches).
          </p>
        )}
      </div>

      {/* Form actions */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !form.name.trim() || !form.attribute.trim() || !form.expression.trim()}
          className="h-9 px-4 text-xs font-semibold rounded-xl text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 flex items-center gap-1.5"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {editingSensor ? t('settings.update') : t('settings.create')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-9 px-4 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          {t('settings.cancel')}
        </button>
        {onSaveTemplate && (
          <div className="ms-auto flex items-center gap-2">
            <input
              type="text"
              value={templateNameInput}
              onChange={(e) => setTemplateNameInput(e.target.value)}
              placeholder={t('sensors.templateNamePlaceholder', 'Template name')}
              className="h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 w-40"
            />
            <button
              type="button"
              disabled={
                savingTemplate ||
                !templateNameInput.trim() ||
                !form.name.trim() ||
                !form.attribute.trim() ||
                !form.expression.trim()
              }
              onClick={async () => {
                setSavingTemplate(true);
                try {
                  await onSaveTemplate(templateNameInput.trim(), form);
                  setTemplateNameInput('');
                } finally {
                  setSavingTemplate(false);
                }
              }}
              className="h-9 px-4 text-xs font-semibold rounded-xl text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-40 flex items-center gap-1.5"
              title={t('sensors.saveAsTemplateHint', 'Save the current sensor configuration as a reusable template for this organization')}
            >
              {savingTemplate ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {t('sensors.saveAsTemplate', 'Save as template')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SensorConfigSection ────────────────────────────────────────────────────────

export default function SensorConfigSection({ device, isAdmin }) {
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const dispatch = useDispatch();
  const orgId = useSelector((s) => s.org?.id);
  const userTemplates = useSelector((s) => s.org?.settings?.sensorTemplates) || [];
  const devicesMap = useSelector((s) => s.devices?.items) || {};
  const livePosition = useSelector((s) => (device?.id ? s.session?.positions?.[device.id] : null));

  // Available attribute keys = whatever this device's latest position
  // actually reports + every key referenced by a built-in template. This
  // turns the freeform Attribute input into an autocomplete combobox so
  // installers don't have to guess "what's my fuel key on this Teltonika".
  // Custom typing is still allowed for keys the device hasn't reported
  // yet (devices that haven't been online since the user opened the page).
  const attributeKeyOptions = (() => {
    const set = new Set();
    if (livePosition?.attributes && typeof livePosition.attributes === 'object') {
      for (const k of Object.keys(livePosition.attributes)) set.add(k);
    }
    for (const tpl of SENSOR_TEMPLATES) if (tpl.attribute) set.add(tpl.attribute);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  })();
  const [sensors, setSensors] = useState(() => parseSensorConfig(device?.attributes?.sensorConfig));
  const [showForm, setShowForm] = useState(false);
  const [editingSensor, setEditingSensor] = useState(null); // sensor object being edited
  const [form, setForm] = useState(emptyForm);
  const [applyTplState, setApplyTplState] = useState(null); // { template, selectedIds, running, results }
  const [bulkRemoveState, setBulkRemoveState] = useState(null); // { sensor, selectedIds, running, results }

  const persistSensors = async (newSensors) => {
    const updatedAttrs = {
      ...device.attributes,
      sensorConfig: JSON.stringify(newSensors),
    };
    // CRITICAL: Always spread full device object (CLAUDE.md Traccar API PUT gotcha)
    const updatedDevice = { ...device, attributes: updatedAttrs };
    const result = await api.updateDevice(device.id, updatedDevice);
    dispatch(devicesActions.update(result));
    setSensors(newSensors);
  };

  const handleAdd = () => {
    setEditingSensor(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const handleEdit = (sensor) => {
    setEditingSensor(sensor);
    setForm(formFromSensor(sensor));
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSensor(null);
    setForm(emptyForm());
  };

  const handleSaved = async (f) => {
    try {
      if (editingSensor) {
        // UPDATE: update computed attribute, then update sensorConfig
        const payload = {
          id: editingSensor.traccarAttributeId,
          description: f.name.trim(),
          attribute: f.attribute.trim(),
          expression: f.expression.trim(),
          type: f.type,
        };
        await api.updateComputedAttribute(editingSensor.traccarAttributeId, payload);
        const entry = buildSensorEntry(f, editingSensor.traccarAttributeId);
        const updated = sensors.map((s) =>
          s.traccarAttributeId === editingSensor.traccarAttributeId ? entry : s
        );
        await persistSensors(updated);
        showToast(t('sensors.saved'));
        handleCancel();
      } else {
        // CREATE: create computed attribute, link permission, save sensorConfig
        const payload = {
          description: f.name.trim(),
          attribute: f.attribute.trim(),
          expression: f.expression.trim(),
          type: f.type,
        };
        const created = await api.createComputedAttribute(payload);
        try {
          await api.linkPermission({ deviceId: device.id, attributeId: created.id });
        } catch (linkErr) {
          // Roll back: delete orphaned computed attribute
          try { await api.deleteComputedAttribute(created.id); } catch { /* ignore */ }
          throw linkErr;
        }
        const entry = buildSensorEntry(f, created.id);
        const updated = [...sensors, entry];
        await persistSensors(updated);
        showToast(t('sensors.saved'));
        handleCancel();
      }
    } catch (err) {
      showToast((t('common.failedSave') || 'Save failed') + ': ' + err.message);
    }
  };

  // ── User templates (org.settings.sensorTemplates) ─────────────────────
  // Refetch-merge-save pattern: OrganizationSettings + LeaderboardBandsCard
  // do the same dance so writes here don't clobber other org.settings
  // slices owned by other surfaces.
  const persistTemplates = async (nextTemplates) => {
    if (!orgId) throw new Error('No org id in session');
    const fresh = await orgsApi.get(orgId);
    const nextSettings = { ...(fresh.settings || {}), sensorTemplates: nextTemplates };
    await orgsApi.update(orgId, { settings: nextSettings });
    dispatch(orgActions.updateOrgSettings(nextSettings));
  };

  const handleSaveTemplate = async (name, f) => {
    try {
      const entry = {
        id: `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name,
        sensor: {
          name: f.name.trim(),
          attribute: f.attribute.trim(),
          expression: f.expression.trim(),
          type: f.type,
          unit: f.unit,
          icon: f.icon,
          validation: f.validation.min !== '' || f.validation.max !== ''
            ? {
                min: f.validation.min !== '' ? Number(f.validation.min) : null,
                max: f.validation.max !== '' ? Number(f.validation.max) : null,
              }
            : null,
          intervals: f.intervals
            .filter((iv) => iv.color)
            .map((iv) => ({
              threshold: iv.threshold !== '' ? Number(iv.threshold) : null,
              color: iv.color,
            })),
        },
      };
      await persistTemplates([...userTemplates, entry]);
      showToast(t('sensors.templateSaved', 'Template saved'));
    } catch (err) {
      showToast((t('common.failedSave') || 'Save failed') + ': ' + err.message);
    }
  };

  const handleRemoveTemplate = async (id) => {
    const tpl = userTemplates.find((tt) => tt.id === id);
    const ok = await confirm({
      title: t('sensors.templateDeleteConfirm', { name: tpl?.name || 'this template' }) ||
        `Delete template "${tpl?.name}"?`,
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await persistTemplates(userTemplates.filter((tt) => tt.id !== id));
      showToast(t('sensors.templateDeleted', 'Template deleted'));
    } catch (err) {
      showToast((t('common.failedSave') || 'Save failed') + ': ' + err.message);
    }
  };

  const handleOpenApplyTemplate = (tpl) => {
    setApplyTplState({ template: tpl, selectedIds: new Set(), running: false, results: null });
  };

  const handleOpenBulkRemove = (sensor) => {
    // Match by attribute key — that's the stable identifier. Sensor name can
    // diverge across devices (admins rename for clarity), but attribute is
    // bound to the hardware key the device actually reports.
    const candidates = Object.values(devicesMap)
      .filter((d) => d && d.id !== device.id && !d.hidden_from_default_list)
      .map((d) => ({
        device: d,
        match: parseSensorConfig(d.attributes?.sensorConfig).find(
          (s) => s.attribute === sensor.attribute,
        ),
      }))
      .filter((row) => row.match);
    setBulkRemoveState({
      sensor,
      candidates,
      selectedIds: new Set(),
      running: false,
      results: null,
    });
  };

  const handleBulkRemoveApply = async () => {
    if (!bulkRemoveState) return;
    const { candidates, selectedIds } = bulkRemoveState;
    setBulkRemoveState((s) => ({ ...s, running: true, results: null }));
    const targets = candidates.filter((c) => selectedIds.has(c.device.id));
    const results = await Promise.all(
      targets.map(async ({ device: target, match }) => {
        try {
          // Mirror handleDelete on the target device: unlink permission,
          // delete computed attribute, rewrite sensorConfig without the entry.
          try {
            await api.unlinkPermission({ deviceId: target.id, attributeId: match.traccarAttributeId });
          } catch { /* may not have a permission row — continue */ }
          try {
            await api.deleteComputedAttribute(match.traccarAttributeId);
          } catch { /* may already be gone — continue */ }
          const remaining = parseSensorConfig(target.attributes?.sensorConfig)
            .filter((s) => s.traccarAttributeId !== match.traccarAttributeId);
          const updatedAttrs = { ...target.attributes, sensorConfig: JSON.stringify(remaining) };
          const result = await api.updateDevice(target.id, { ...target, attributes: updatedAttrs });
          dispatch(devicesActions.update(result));
          return { deviceId: target.id, ok: true };
        } catch (err) {
          return { deviceId: target.id, ok: false, error: err?.message || 'Unknown error' };
        }
      })
    );
    setBulkRemoveState((s) => ({ ...s, running: false, results }));
    const okCount = results.filter((r) => r.ok).length;
    showToast(
      t('sensors.bulkRemoveDone', 'Removed from {{ok}}/{{total}} devices', {
        ok: okCount,
        total: results.length,
      })
    );
  };

  const handleApplyToDevices = async () => {
    if (!applyTplState) return;
    const { template, selectedIds } = applyTplState;
    setApplyTplState((s) => ({ ...s, running: true, results: null }));
    const targetIds = Array.from(selectedIds);
    const results = await Promise.all(
      targetIds.map(async (deviceId) => {
        const target = devicesMap[deviceId];
        if (!target) return { deviceId, ok: false, error: 'Device not in Redux' };
        try {
          // 1. Create computed attribute server-side.
          const created = await api.createComputedAttribute({
            description: template.sensor.name,
            attribute: template.sensor.attribute,
            expression: template.sensor.expression,
            type: template.sensor.type,
          });
          // 2. Link to target device.
          try {
            await api.linkPermission({ deviceId, attributeId: created.id });
          } catch (linkErr) {
            try { await api.deleteComputedAttribute(created.id); } catch { /* ignore */ }
            throw linkErr;
          }
          // 3. Append to target device's sensorConfig.
          const existing = parseSensorConfig(target.attributes?.sensorConfig);
          const newEntry = { ...template.sensor, traccarAttributeId: created.id };
          const nextSensors = [...existing, newEntry];
          const updatedAttrs = { ...target.attributes, sensorConfig: JSON.stringify(nextSensors) };
          const result = await api.updateDevice(deviceId, { ...target, attributes: updatedAttrs });
          dispatch(devicesActions.update(result));
          return { deviceId, ok: true };
        } catch (err) {
          return { deviceId, ok: false, error: err?.message || 'Unknown error' };
        }
      })
    );
    setApplyTplState((s) => ({ ...s, running: false, results }));
    const okCount = results.filter((r) => r.ok).length;
    showToast(
      t('sensors.applyTemplateDone', 'Applied to {{ok}}/{{total}} devices', {
        ok: okCount,
        total: results.length,
      })
    );
  };

  const handleDelete = async (sensor) => {
    const ok = await confirm({
      title: t('sensors.deleteConfirm', { name: sensor.name }),
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await api.unlinkPermission({ deviceId: device.id, attributeId: sensor.traccarAttributeId });
    } catch { /* permission may not exist — continue */ }
    try {
      await api.deleteComputedAttribute(sensor.traccarAttributeId);
    } catch { /* may already be gone — continue */ }
    const updated = sensors.filter((s) => s.traccarAttributeId !== sensor.traccarAttributeId);
    await persistSensors(updated);
    showToast(t('sensors.deleted'));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className={sectionClass}>{t('sensors.title')}</h4>
        {!showForm && (
          <button
            type="button"
            onClick={handleAdd}
            className="h-7 px-3 text-[10px] font-semibold rounded-lg text-white flex items-center gap-1"
            style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', boxShadow: '0 2px 8px rgba(59,130,246,0.3)' }}
          >
            <Plus size={11} />
            {t('sensors.addSensor')}
          </button>
        )}
      </div>

      {/* Sensor list */}
      {sensors.length === 0 && !showForm ? (
        <EmptyState
          icon={Activity}
          title={t('sensors.noSensors')}
          subtitle={t('sensors.noSensorsDesc')}
        />
      ) : (
        <div className="space-y-2">
          {sensors.map((sensor) => {
            const IconComp = resolveSensorIcon(sensor.icon);
            const intervalDots = (sensor.intervals || []).filter((iv) => iv.color);
            return (
              <div
                key={sensor.traccarAttributeId}
                className="flex items-center gap-3 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
              >
                <IconComp size={15} className="text-slate-500 dark:text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">{sensor.name}</span>
                    {sensor.unit && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-mono">
                        {sensor.unit}
                      </span>
                    )}
                    {/* Color interval dots */}
                    {intervalDots.length > 0 && (
                      <div className="flex items-center gap-1 ms-1">
                        {intervalDots.map((iv, i) => (
                          <span
                            key={i}
                            className="w-2.5 h-2.5 rounded-full border border-white/20"
                            style={{ background: colorStatusToHex(iv.color) }}
                            title={iv.threshold != null ? `≥${iv.threshold}: ${iv.color}` : iv.color}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono truncate mt-0.5">{sensor.expression}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(sensor)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    title={t('sensors.editSensor')}
                  >
                    <Edit3 size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenBulkRemove(sensor)}
                    className="p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 text-slate-400 hover:text-amber-600 transition-colors"
                    title={t('sensors.bulkRemoveFromDevices', 'Remove this sensor from other devices in the fleet…')}
                  >
                    <Layers size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(sensor)}
                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 transition-colors"
                    title={t('sensors.deleteSensor')}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sensor form (inline) */}
      {showForm && (
        <SensorForm
          device={device}
          form={form}
          setForm={setForm}
          editingSensor={editingSensor}
          onSaved={handleSaved}
          onCancel={handleCancel}
          isAdmin={isAdmin}
          userTemplates={userTemplates}
          onSaveTemplate={handleSaveTemplate}
          onRemoveTemplate={handleRemoveTemplate}
          onApplyTemplateToDevices={handleOpenApplyTemplate}
          attributeKeyOptions={attributeKeyOptions}
        />
      )}

      {applyTplState && (
        <ApplyTemplateDialog
          state={applyTplState}
          devices={devicesMap}
          onToggleId={(id) => {
            setApplyTplState((s) => {
              const ns = new Set(s.selectedIds);
              if (ns.has(id)) ns.delete(id); else ns.add(id);
              return { ...s, selectedIds: ns };
            });
          }}
          onSelectAll={(ids) => setApplyTplState((s) => ({ ...s, selectedIds: new Set(ids) }))}
          onClear={() => setApplyTplState((s) => ({ ...s, selectedIds: new Set() }))}
          onApply={handleApplyToDevices}
          onClose={() => setApplyTplState(null)}
          excludeDeviceId={device?.id}
        />
      )}

      {bulkRemoveState && (
        <BulkRemoveSensorDialog
          state={bulkRemoveState}
          onToggleId={(id) => {
            setBulkRemoveState((s) => {
              const ns = new Set(s.selectedIds);
              if (ns.has(id)) ns.delete(id); else ns.add(id);
              return { ...s, selectedIds: ns };
            });
          }}
          onSelectAll={(ids) => setBulkRemoveState((s) => ({ ...s, selectedIds: new Set(ids) }))}
          onClear={() => setBulkRemoveState((s) => ({ ...s, selectedIds: new Set() }))}
          onApply={handleBulkRemoveApply}
          onClose={() => setBulkRemoveState(null)}
        />
      )}
    </div>
  );
}

// ── ApplyTemplateDialog ────────────────────────────────────────────────────────

function ApplyTemplateDialog({ state, devices, onToggleId, onSelectAll, onClear, onApply, onClose, excludeDeviceId }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const list = Object.values(devices || {})
    .filter((d) => d && !d.hidden_from_default_list && d.id !== excludeDeviceId)
    .filter((d) => !search.trim() || (d.name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const visibleIds = list.map((d) => d.id);
  const { template, selectedIds, running, results } = state;
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">
              {t('sensors.applyTemplateTitle', 'Apply template to other devices')}
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              {t('sensors.applyTemplateSubtitle', 'Template')}: <span className="font-semibold text-blue-600 dark:text-blue-300">{template.name}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
            <X size={14} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 space-y-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('sensors.searchDevices', 'Search devices…')}
            className="w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10"
          />
          <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
            <span>{t('sensors.selectedCount', '{{n}} selected', { n: selectedIds.size })}</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => onSelectAll(visibleIds)} className="text-blue-600 dark:text-blue-400 hover:underline" disabled={allSelected || !visibleIds.length}>
                {t('sensors.selectAll', 'Select all')}
              </button>
              <button type="button" onClick={onClear} className="text-slate-500 hover:underline" disabled={!selectedIds.size}>
                {t('sensors.clear', 'Clear')}
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1">
          {list.length === 0 && (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">
              {t('sensors.noOtherDevices', 'No other devices match your filter.')}
            </p>
          )}
          {list.map((d) => {
            const r = results ? results.find((x) => x.deviceId === d.id) : null;
            return (
              <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.has(d.id)}
                  onChange={() => onToggleId(d.id)}
                  disabled={running}
                  className="rounded border-slate-300 text-blue-500 focus:ring-blue-400"
                />
                <span className="text-xs text-slate-700 dark:text-slate-200 flex-1 truncate">{d.name || `Device #${d.id}`}</span>
                {r?.ok && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">{t('sensors.applied', 'Applied')}</span>}
                {r && !r.ok && <span className="text-[10px] text-red-500 truncate max-w-[160px]" title={r.error}>{r.error || 'Failed'}</span>}
              </label>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-4 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
            {t('sensors.close', 'Close')}
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={running || selectedIds.size === 0}
            className="h-9 px-4 text-xs font-semibold rounded-xl text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 flex items-center gap-1.5"
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Activity size={13} />}
            {t('sensors.applyToSelected', 'Apply to {{n}}', { n: selectedIds.size })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── BulkRemoveSensorDialog ─────────────────────────────────────────────────────
// Inverse of ApplyTemplateDialog. Operates on the set of devices that ALREADY
// have a sensor matching the given attribute key, and lets the admin remove
// the sensor from any subset of them in one pass. The current device is never
// shown (delete it from the local list via the regular Trash button).

function BulkRemoveSensorDialog({ state, onToggleId, onSelectAll, onClear, onApply, onClose }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { sensor, candidates, selectedIds, running, results } = state;
  const filtered = (candidates || [])
    .filter((row) => !search.trim() || (row.device.name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.device.name || '').localeCompare(b.device.name || ''));
  const visibleIds = filtered.map((r) => r.device.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">
              {t('sensors.bulkRemoveTitle', 'Remove sensor from devices')}
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              {t('sensors.bulkRemoveSubtitle', 'Sensor')}: <span className="font-semibold text-amber-600 dark:text-amber-300">{sensor.name}</span>
              <span className="ms-2 text-slate-400 font-mono">({sensor.attribute})</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
            <X size={14} />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 space-y-2">
          {candidates.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('sensors.bulkRemoveNoCandidates', 'No other devices in this org have a sensor on attribute {{attr}}.', { attr: sensor.attribute })}
            </p>
          ) : (
            <>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('sensors.searchDevices', 'Search devices…')}
                className="w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10"
              />
              <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                <span>{t('sensors.selectedCount', '{{n}} selected', { n: selectedIds.size })}</span>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => onSelectAll(visibleIds)} className="text-blue-600 dark:text-blue-400 hover:underline" disabled={allSelected || !visibleIds.length}>
                    {t('sensors.selectAll', 'Select all')}
                  </button>
                  <button type="button" onClick={onClear} className="text-slate-500 hover:underline" disabled={!selectedIds.size}>
                    {t('sensors.clear', 'Clear')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1">
          {filtered.map(({ device: d, match }) => {
            const r = results ? results.find((x) => x.deviceId === d.id) : null;
            return (
              <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.has(d.id)}
                  onChange={() => onToggleId(d.id)}
                  disabled={running}
                  className="rounded border-slate-300 text-blue-500 focus:ring-blue-400"
                />
                <span className="text-xs text-slate-700 dark:text-slate-200 flex-1 truncate">{d.name || `Device #${d.id}`}</span>
                <span className="text-[10px] text-slate-400 font-mono" title={t('sensors.bulkRemoveSensorName', 'Configured as')}>
                  {match?.name}
                </span>
                {r?.ok && <span className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold">{t('sensors.removed', 'Removed')}</span>}
                {r && !r.ok && <span className="text-[10px] text-red-500 truncate max-w-[160px]" title={r.error}>{r.error || 'Failed'}</span>}
              </label>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-4 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
            {t('sensors.close', 'Close')}
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={running || selectedIds.size === 0}
            className="h-9 px-4 text-xs font-semibold rounded-xl text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-40 flex items-center gap-1.5"
          >
            {running ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {t('sensors.bulkRemoveFromSelected', 'Remove from {{n}}', { n: selectedIds.size })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SyntaxCheatSheetPopover ────────────────────────────────────────────────────
// Compact JEXL cheat sheet, anchored next to the Expression field's ⓘ icon.
// Each row is click-to-insert into the textarea via the onInsert callback —
// the user doesn't have to memorise the syntax, they can browse + click.
// Each row also has a Copy button for power users who want the snippet in
// the clipboard for some other context.

const SYNTAX_CHEAT_SHEET = [
  { label: 'Passthrough',           snippet: 'attr',                                hint: 'Sensor mirrors the raw attribute value' },
  { label: 'Scale',                 snippet: 'adc1 * 0.024',                        hint: 'Analog reading (0–4095) → percent (0–100)' },
  { label: 'Offset',                snippet: 'coolantTemp - 40',                    hint: 'Many OBD readings are offset by 40°C' },
  { label: 'Scale + offset',        snippet: 'voltage * 0.1 + 2.5',                 hint: 'Voltage divider with bias — same as Quick Transforms below' },
  { label: 'Boolean invert',        snippet: '!din2',                               hint: 'Active-low ignition / door / panic — same as the Invert toggle' },
  { label: 'Boolean compound',      snippet: 'din2 && ignition',                    hint: 'Combine two attributes — e.g. "seatbelt buckled while engine running"' },
  { label: 'Position accessor (s)', snippet: 'position.getString("io1")',           hint: 'Read a raw protocol field as a string' },
  { label: 'Position accessor (n)', snippet: 'position.getDouble("fuel")',          hint: 'Force-cast a raw protocol field to a number' },
  { label: 'Ternary',               snippet: 'speed > 90 ? "fast" : "ok"',          hint: 'Categorise a numeric range into labels (Type = String)' },
  { label: 'Math',                  snippet: 'Math.round(rpm * 0.25)',              hint: 'Quarter-rev encoders, rounding to nearest integer' },
];

function SyntaxCheatSheetPopover({ onClose, onInsert }) {
  const { t } = useTranslation();
  const [copiedIdx, setCopiedIdx] = useState(null);

  const copy = async (snippet, idx) => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1200);
    } catch { /* clipboard blocked — fall through silently */ }
  };

  return (
    <div className="absolute z-30 top-7 start-0 w-[420px] max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">
          {t('sensors.syntaxCheatSheetTitle', 'JEXL syntax — click any row to insert')}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          aria-label="Close"
        >
          <X size={12} />
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
        {SYNTAX_CHEAT_SHEET.map((row, idx) => (
          <div key={idx} className="px-3 py-2 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 group">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => onInsert(row.snippet)}
                className="flex-1 text-start"
                title={t('sensors.syntaxInsertHint', 'Click to insert into Expression')}
              >
                <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {row.label}
                </div>
                <code className="block text-[11.5px] font-mono text-blue-700 dark:text-blue-300 mt-0.5">
                  {row.snippet}
                </code>
              </button>
              <button
                type="button"
                onClick={() => copy(row.snippet, idx)}
                className="text-[10px] px-2 py-0.5 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 opacity-0 group-hover:opacity-100 transition-opacity"
                title={t('sensors.syntaxCopy', 'Copy to clipboard')}
              >
                {copiedIdx === idx ? t('sensors.syntaxCopied', 'Copied') : t('sensors.syntaxCopy', 'Copy')}
              </button>
            </div>
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
              {row.hint}
            </div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40">
        <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-snug">
          {t(
            'sensors.syntaxFooter',
            'Quick Transforms above (Scale × value + Offset / Invert) cover the most common cases — only use the raw expression for formulas they can\'t express.',
          )}
        </p>
      </div>
    </div>
  );
}

